// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import {
    Button,
    Spinner,
    Textarea,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
    PopoverSurface,
    Popover,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
} from '@fluentui/react-components';
import {
    AttachRegular,
    MicRegular,
    SendRegular,
    DocumentDatabaseRegular,
    DismissCircle20Regular,
    GlobeSearchRegular,
    GlobeSearchFilled,
} from '@fluentui/react-icons';
import debug from 'debug';
import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import React, { useRef, useState } from 'react';
import { Constants } from '../../Constants';
import { COPY } from '../../assets/strings';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { useFile } from '../../libs/hooks';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert, setSelectedChatWindowTab } from '../../redux/features/app/appSlice';
import {
    editConversationInput,
    updateBotResponseStatus,
} from '../../redux/features/conversations/conversationsSlice';
import { Alerts } from '../shared/Alerts';
import { getErrorDetails, getTextPositionCoordinates, truncateTextAdvanced } from '../utils/TextUtils';
import { SpeechService } from './../../libs/services/SpeechService';
import { updateUserIsTyping } from './../../redux/features/conversations/conversationsSlice';
import { ChatStatus } from './ChatStatus';
import { FeatureKeys } from '../../redux/features/app/AppState';
import { ChatMemorySource } from '../../libs/models/ChatMemorySource';
//import { iManageDialog } from './chat-list/dialogs/iManageDialog';

const log = debug(Constants.debug.root).extend('chat-input');

const useClasses = makeStyles({
    surfaceContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '105em',
        ...shorthands.margin(tokens.spacingVerticalNone, tokens.spacingHorizontalM),
    },
    typingIndicator: {
        maxHeight: '28px',
    },
    content: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    },
    input: {
        width: '100%',
    },
    textarea: {
        maxHeight: '300px',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
    },
    essentials: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 'auto', // align to right
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
    },
    dragAndDrop: {
        ...shorthands.border(tokens.strokeWidthThick, ' solid', tokens.colorBrandStroke1),
        ...shorthands.padding('8px'),
        textAlign: 'center',
        backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorBrandForeground1,
        caretColor: 'transparent',
    },
    promptSetting: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        fontStyle: 'italic',
        color: '#222222',
        ...shorthands.margin(tokens.spacingVerticalXS, tokens.spacingHorizontalM),
    },
    promptDismissIcon: {
        marginLeft: tokens.spacingHorizontalXS,
        cursor: 'pointer',
        color: 'inherit',
        '&:hover': {
            color: '#982c57',
        },
        paddingTop: '2px',
    },
    contentHeader: {
        marginTop: '0',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    documentBoxesContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('8px'),
        marginBottom: '8px',
    },
    documentBox: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('4px'),
        ...shorthands.padding('4px 8px'),
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.border(`1px solid ${tokens.colorNeutralStroke1}`),
    },
});

interface ChatInputProps {
    isDraggingOver?: boolean;
    onDragLeave: React.DragEventHandler<HTMLDivElement | HTMLTextAreaElement>;
    onSubmit: (options: GetResponseOptions) => Promise<void>;
    removePromptFromChat: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
    isDraggingOver,
    onDragLeave,
    onSubmit,
    removePromptFromChat,
}) => {
    const classes = useClasses();
    const { instance, inProgress } = useMsal();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const fileHandler = useFile();
    const { features } = useAppSelector((state: RootState) => state.app);

    const [value, setValue] = useState('');
    const [recognizer, setRecognizer] = useState<speechSdk.SpeechRecognizer>();
    const [isListening, setIsListening] = useState(false);
    const { importingDocuments } = conversations[selectedId];

    const [isiManageDialogOpen, setIsiManageDialogOpen] = useState(false);

    const chat = useChat();
    const [resources, setResources] = React.useState<ChatMemorySource[]>([]);
    const [showResourcePopup, setShowResourcePopup] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

    const [documentBoxes, setDocumentBoxes] = useState<Array<{ id: string; name: string; position: number }>>([]);

    const [isOpen, setIsOpen] = useState(false);
    const [icon, setIcon] = useState(false);
    const [webSearchOn, setWebSearchOn] = useState(false);
    const handleOpenChange = (_e: any, data: { open: boolean }) => {
        setIsOpen(data.open);
    };

    const documentFileRef = useRef<HTMLInputElement | null>(null);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        textAreaRef.current?.focus();
    }, [selectedId]);

    React.useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'inherit';
            textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
        }
    }, [value]);

    React.useEffect(() => {
        async function initSpeechRecognizer() {
            const speechService = new SpeechService();
            const response = await speechService.getSpeechTokenAsync(
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
            if (response.isSuccess) {
                const recognizer = speechService.getSpeechRecognizerAsyncWithValidKey(response);
                setRecognizer(recognizer);
            }
        }

        initSpeechRecognizer().catch((e) => {
            const errorDetails = getErrorDetails(e);
            const errorMessage = `Unable to initialize speech recognizer. Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    }, [dispatch, instance, inProgress]);

    React.useEffect(() => {
        const chatState = conversations[selectedId];
        setValue(chatState.disabled ? COPY.CHAT_DELETED_MESSAGE() : chatState.input);
    }, [conversations, selectedId]);

    React.useEffect(() => {
        if (!conversations[selectedId].disabled) {
            const importingResources = importingDocuments
                ? importingDocuments.map((document, index) => {
                      return {
                          id: `in-progress-${index}`,
                          chatId: selectedId,
                          sourceType: 'N/A',
                          name: document,
                          sharedBy: 'N/A',
                          createdOn: 0,
                          size: 0,
                      } as ChatMemorySource;
                  })
                : [];
            setResources(importingResources);

            void chat.getChatMemorySources(selectedId).then((sources) => {
                setResources([...importingResources, ...sources]);
            });
        }
    }, [importingDocuments, selectedId]);

    React.useEffect(() => {
        setWebSearchOn(false);
        setIcon(false);
    }, [conversations[selectedId].id]);

    React.useEffect(() => {
        const textArea = textAreaRef.current;
        if (textArea) {
            const text = conversations[selectedId].input;
            const matches = text.match(/\[\[.*?\]\]/g);
            if (matches) {
                matches.forEach((match) => {
                    const [name, id] = match.slice(2, -2).split('|');
                    setDocumentBoxes((prev) => {
                        if (!prev.find((box) => box.id === id)) {
                            return [
                                ...prev,
                                {
                                    id,
                                    name,
                                    position: text.indexOf(match),
                                },
                            ];
                        }
                        return prev;
                    });
                });
            }
        }
    }, [conversations[selectedId].input]);

    const handleKeyUp = (event: { key: string; target: EventTarget }) => {
        if (event.key === '#') {
            const textArea = event.target as HTMLTextAreaElement;
            const cursorPosition = textArea.selectionStart;

            const coordinates = getTextPositionCoordinates(textArea, cursorPosition);

            setPopupPosition({
                x: coordinates.x,
                y: coordinates.y,
            });
            setShowResourcePopup(true);
        }
    };

    const handleResourceSelect = (resourceName: string, resourceId: string) => {
        const textArea = textAreaRef.current;
        if (!textArea) return;

        const cursorPosition = textArea.selectionStart;
        const currentInput = conversations[selectedId].input;

        const documentReference = `[${resourceName}]`;
        const newInput =
            currentInput.substring(0, cursorPosition - 1) + documentReference + currentInput.substring(cursorPosition);

        setDocumentBoxes((prev) => [
            ...prev,
            {
                id: resourceId,
                name: resourceName,
                position: cursorPosition - 1,
            },
        ]);

        dispatch(editConversationInput({ id: selectedId, newInput }));
        setShowResourcePopup(false);

        textArea.focus();
        const newCursorPosition = cursorPosition - 1 + documentReference.length;
        textArea.setSelectionRange(newCursorPosition, newCursorPosition);
    };

    const removeDocumentBox = (boxId: string) => {
        const textArea = textAreaRef.current;
        if (!textArea) return;

        const documentToRemove = documentBoxes.find((box) => box.id === boxId);
        if (!documentToRemove) return;

        const currentInput = conversations[selectedId].input;

        const documentReference = `[${documentToRemove.name}]`;
        const newInput = currentInput.replace(documentReference, '');

        setDocumentBoxes((prev) => prev.filter((box) => box.id !== boxId));

        dispatch(editConversationInput({ id: selectedId, newInput }));

        textArea.focus();
    };

    const handleSpeech = () => {
        setIsListening(true);
        if (recognizer) {
            recognizer.recognizeOnceAsync((result) => {
                if (result.reason === speechSdk.ResultReason.RecognizedSpeech) {
                    if (result.text && result.text.length > 0) {
                        handleSubmit(result.text);
                    }
                }
                setIsListening(false);
            });
        }
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (value.trim() === '' && documentBoxes.length === 0) {
            return;
        }

        const resourceIds = documentBoxes.map((box) => box.id);

        setValue('');
        setDocumentBoxes([]);
        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Calling the kernel' }));

        onSubmit({
            value,
            messageType,
            chatId: selectedId,
            selectedOptions: resourceIds,
            useWebSearch: webSearchOn,
        }).catch((error) => {
            const message = `Error submitting chat input: ${(error as Error).message}`;
            log(message);
            dispatch(
                addAlert({
                    type: AlertType.Error,
                    message,
                }),
            );
        });
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        onDragLeave(e);
        void fileHandler.handleImport(selectedId, documentFileRef, false, undefined, e.dataTransfer.files);
    };

    const openiManageDialog = () => {
        if (!isiManageDialogOpen) {
            setIsiManageDialogOpen(true);
        }
    };

    /*const closeiManageDialog = () => {
        setIsiManageDialogOpen(false);
    };*/

    //const handleiManageImport = () => {
    /*setIsListening(true);
        if (recognizer) {
            recognizer.recognizeOnceAsync((result) => {
                if (result.reason === speechSdk.ResultReason.RecognizedSpeech) {
                    if (result.text && result.text.length > 0) {
                        handleSubmit(result.text);
                    }
                }
                setIsListening(false);
            });
        }*/
    //};

    return (
        <div className={classes.root}>
            <div className={classes.typingIndicator}>
                <ChatStatus />
            </div>
            <Alerts />
            {conversations[selectedId].promptId ? (
                <div className={classes.promptSetting}>
                    <Text>Using Prompt Settings For: {conversations[selectedId].promptName}</Text>
                    <DismissCircle20Regular className={classes.promptDismissIcon} onClick={removePromptFromChat} />
                </div>
            ) : null}
            <div className={classes.documentBoxesContainer}>
                {documentBoxes.map((box) => (
                    <div key={box.id} className={classes.documentBox}>
                        <span>{box.name}</span>
                        <Button
                            icon={<DismissCircle20Regular />}
                            appearance="transparent"
                            onClick={() => {
                                removeDocumentBox(box.id);
                            }}
                        />
                    </div>
                ))}
            </div>
            <div className={classes.content}>
                <Textarea
                    title="Chat input"
                    aria-label="Chat input field. Click enter to submit input."
                    ref={textAreaRef}
                    id="chat-input"
                    resize="vertical"
                    disabled={conversations[selectedId].disabled}
                    textarea={{
                        className: isDraggingOver
                            ? mergeClasses(classes.dragAndDrop, classes.textarea)
                            : classes.textarea,
                    }}
                    className={classes.input}
                    value={conversations[selectedId].input}
                    onDrop={handleDrop}
                    onFocus={() => {
                        // update the locally stored value to the current value
                        const chatInput = document.getElementById('chat-input');
                        if (chatInput) {
                            setValue((chatInput as HTMLTextAreaElement).value);
                        }
                        // User is considered typing if the input is in focus
                        if (activeUserInfo) {
                            dispatch(
                                updateUserIsTyping({ userId: activeUserInfo.id, chatId: selectedId, isTyping: true }),
                            );
                        }
                    }}
                    onChange={(_event, data) => {
                        if (isDraggingOver) {
                            return;
                        }
                        if (data.value.length === 0) {
                            removePromptFromChat();
                        }

                        dispatch(
                            editConversationInput({
                                id: selectedId,
                                newInput: data.value,
                            }),
                        );
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            handleSubmit(value);
                        }
                    }}
                    onBlur={() => {
                        // User is considered not typing if the input is not  in focus
                        if (activeUserInfo) {
                            dispatch(
                                updateUserIsTyping({ userId: activeUserInfo.id, chatId: selectedId, isTyping: false }),
                            );
                        }
                    }}
                    onKeyUp={handleKeyUp}
                />
            </div>
            {showResourcePopup && (
                <Popover
                    open={showResourcePopup}
                    onOpenChange={(_e, data) => {
                        setShowResourcePopup(data.open);
                    }}
                    withArrow
                    positioning={{
                        position: 'above',
                        target: {
                            getBoundingClientRect: () => ({
                                x: popupPosition.x,
                                y: popupPosition.y,
                                top: popupPosition.y,
                                left: popupPosition.x,
                                right: popupPosition.x,
                                bottom: popupPosition.y,
                                width: 0,
                                height: 0,
                            }),
                        },
                    }}
                >
                    <PopoverSurface tabIndex={-1}>
                        <div className={classes.surfaceContainer}>
                            {resources.map((resource) => (
                                <Button
                                    key={resource.id}
                                    appearance="subtle"
                                    onClick={() => {
                                        handleResourceSelect(resource.name, resource.id);
                                    }}
                                >
                                    {truncateTextAdvanced(resource.name, 50)}
                                </Button>
                            ))}
                        </div>
                    </PopoverSurface>
                </Popover>
            )}
            <div className={classes.controls}>
                <div className={classes.functional}>
                    <Button
                        disabled={
                            conversations[selectedId].disabled || (importingDocuments && importingDocuments.length > 0)
                        }
                        appearance="transparent"
                        icon={<AttachRegular />}
                        onClick={() => dispatch(setSelectedChatWindowTab('documents'))}
                        title="Attach file"
                        aria-label="Attach file button"
                    />
                    {importingDocuments && importingDocuments.length > 0 && <Spinner size="tiny" />}
                </div>
                <p>
                    Click the attachment icon to upload files, or alternatively drag and drop multiple files into the
                    area above. <i>(10MB file limit)</i>
                </p>
                <div className={classes.essentials}>
                    {features[FeatureKeys.iManage].enabled && (
                        <Button
                            appearance="transparent"
                            title="Import from iManage"
                            icon={<DocumentDatabaseRegular />}
                            onClick={openiManageDialog}
                        />
                    )}
                    {recognizer && (
                        <Button
                            appearance="transparent"
                            disabled={conversations[selectedId].disabled || isListening}
                            icon={<MicRegular />}
                            onClick={handleSpeech}
                        />
                    )}
                    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="transparent"
                                icon={icon ? <GlobeSearchFilled primaryFill="#00C389" /> : <GlobeSearchRegular />}
                            />
                        </DialogTrigger>
                        <DialogSurface>
                            <DialogBody>
                                <DialogTitle>Web Search</DialogTitle>
                                <DialogContent className={classes.content}>
                                    <Text>Do you wish to turn on WebSearch?</Text>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        appearance="secondary"
                                        onClick={() => {
                                            setIsOpen(false);
                                            setIcon(!webSearchOn);
                                            setWebSearchOn(!webSearchOn);
                                        }}
                                    >
                                        Turn {webSearchOn ? 'Off' : 'On'} Web Search
                                    </Button>
                                </DialogActions>
                            </DialogBody>
                        </DialogSurface>
                    </Dialog>
                    <Button
                        title="Submit"
                        aria-label="Submit message"
                        appearance="transparent"
                        icon={<SendRegular />}
                        onClick={() => {
                            handleSubmit(value);
                        }}
                        disabled={conversations[selectedId].disabled}
                    />
                </div>
            </div>
        </div>
    );
};
